import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CustomerSearchRequest, CustomerService } from 'api';
import { Observable, Subject, map, shareReplay, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import {
  KeydownSearchDirective,
  CommonModule as SSCommonModule,
  regularExpressions,
} from 'common';
import { ButtonComponent } from 'ui-elements/button';
import { ssSelectImports, SelectData } from 'ui-elements/select';
import { FormFieldModule } from 'ui-elements/form-field';
import { BaseInputDirective } from 'ui-elements/form/input/base-input.directive';

@Component({
  selector: 'ss-stx-shared-customer-search-form',
  templateUrl: './customer-search-form.component.html',
  styleUrls: ['./customer-search-form.component.scss'],
  imports: [
    CommonModule,
    ButtonComponent,
    ReactiveFormsModule,
    SSCommonModule,
    ssSelectImports,
    FormFieldModule,
    BaseInputDirective,
    KeydownSearchDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerSearchFormComponent implements OnInit, OnDestroy {
  private customerService = inject(CustomerService);
  readonly fb = inject(UntypedFormBuilder);
  private readonly route = inject(ActivatedRoute);

  customerAttributes$?: Observable<SelectData[]>;
  //#region Inputs/Outputs Properties
  @Output() formChanged = new EventEmitter<CustomerSearchRequest>();
  @Output() searchForm = new EventEmitter<CustomerSearchRequest>();
  //#endregion

  //#region Private Properties
  private destroy$ = new Subject<boolean>();

  form = this.fb.group({
    phoneNumber: this.fb.control(
      null,
      Validators.pattern(regularExpressions.onlyNumbersPattern)
    ),
    companyName: this.fb.control(null, Validators.maxLength(50)),
    firstName: this.fb.control(null, Validators.maxLength(20)),
    lastName: this.fb.control(null, Validators.maxLength(20)),
    customerNumber: this.fb.control(null, [
      Validators.pattern(regularExpressions.onlyNumbersPattern),
      Validators.maxLength(50),
    ]),
    zipCode: this.fb.control(null, [
      Validators.maxLength(12),
      Validators.pattern(regularExpressions.genericZipCode),
    ]),
    emailAddress: this.fb.control(null, [
      Validators.pattern(regularExpressions.emailPattern),
    ]),
    customerAttributes: this.fb.control(null),
  });
  //#endregion

  //#region Public Properties
  openAdvancedSearch = false;
  //#endregion

  //#region Lifecycle Hooks
  constructor() {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.form.patchValue(params);
      });

    this.initSubscriptions();
  }

  ngOnInit(): void {
    this.customerAttributes$ = this.customerService
      .getAllMarketingAttributes()
      .pipe(
        map((attributes) =>
          attributes.map((attribute) => ({ name: attribute, value: attribute }))
        ),
        shareReplay()
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
  //#endregion

  //#region Public Methods
  get isThereAnyAdvancedSearchField(): boolean {
    return (
      this.form.get('customerNumber')?.value ||
      this.form.get('zipCode')?.value ||
      this.form.get('emailAddress')?.value ||
      this.form.get('customerAttributes')?.value
    );
  }

  clearSearchFields(): void {
    this.form.reset();
  }

  openCollapseSearch(): void {
    this.openAdvancedSearch = !this.openAdvancedSearch;
  }

  search(): void {
    this.searchForm.emit(this.form.value);
  }

  //#endregion

  //#region Private Methods
  private initSubscriptions() {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe({
      next: (value) => this.formChanged.emit(value),
    });
  }
  //#endregion
}
